import React from 'react'
import cx from 'classnames'
import * as R from 'ramda'
import { t } from 'ttag'

import { Box } from 'components/layout/Box/Box'
import { Form } from 'components/shared/Form/Form'
import { Icon, ChevronDownIcon, ChevronUpIcon } from 'components/shared/Icon/Icon'

import { ASSESSMENT_GROUP_FIELD, SCORE_FIELD, SKILL_RANGE } from '../../model'

import styles from './SummaryAssessmentForm.module.scss'

const getSectionByKey = (sections, key) =>
  R.find(R.propEq('key', key), sections ?? [])

export const SummaryAssessmentForm = ({
  className = undefined,
  control,
  fields,
  skillTitles,
  watch,
  sections = [],
  questionComments = {},
  onCommentChange,
}) => {
  const length = R.length(fields)
  const [expandedNotes, setExpandedNotes] = React.useState({})

  const handleNotesToggle = React.useCallback(sectionKey => () => {
    setExpandedNotes(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))
  }, [])

  const handleNotesChange = React.useCallback(
    (sectionKey, questionId) => e => {
      onCommentChange?.(sectionKey, questionId, e.target.value)
    },
    [onCommentChange],
  )

  return (
    <Form
      className={cx(styles.assessment, { [className]: Boolean(className) })}
    >
      {R.pipe(
        R.addIndex(R.map)(({ id, key }, index) => {
          const count = index + 1
          const section = getSectionByKey(sections, key)
          const questions = R.filter(q => q.id != null, section?.questions ?? [])

          const score = watch(
            `${ASSESSMENT_GROUP_FIELD}[${index}].${SCORE_FIELD}`,
          )

          return (
            <Box className={styles.content} direction="column" key={id}>
              <span className={styles.count}>{t`${count} of ${length}`}</span>
              <h3 className={styles.title}>{skillTitles[key]}</h3>
              <Box align="center" className={styles.score} justify="center">
                {R.pipe(
                  R.add(1),
                  R.range(1),
                  R.map(value => (
                    <Form.Field
                      className={styles.field}
                      id={`${id}-score-${value}`}
                      field="score"
                      key={value}
                      label={value}
                      labelSize="medium"
                      name={`${ASSESSMENT_GROUP_FIELD}[${index}].${SCORE_FIELD}`}
                      score={value}
                      isScoreActive={R.equals(Number(score), value)}
                      {...{ control }}
                    />
                  )),
                )(SKILL_RANGE)}
              </Box>
              {R.gt(questions.length, 0) ? (
                <Box className={styles.notesDropdown} direction="column">
                  <button
                    type="button"
                    className={styles.notesTrigger}
                    onClick={handleNotesToggle(key)}
                    aria-expanded={expandedNotes[key]}
                  >
                    <span className={styles.notesTriggerLabel}>{t`Notes`}</span>
                    <Icon
                      icon={
                        expandedNotes[key] ? ChevronUpIcon : ChevronDownIcon
                      }
                      size={20}
                    />
                  </button>
                  {expandedNotes[key] ? (
                    <Box className={styles.notesContent} direction="column">
                      {R.map(({ id: questionId, title: questionTitle }) => {
                        const noteKey =
                          questionId != null ? String(questionId) : null
                        if (!noteKey) return null
                        const noteValue =
                          R.path([key, noteKey], questionComments) ?? ''
                        return (
                          <Box
                            key={noteKey}
                            className={styles.notesSection}
                            direction="column"
                          >
                            <label
                              className={styles.notesLabel}
                              htmlFor={`notes-${noteKey}`}
                            >
                              {questionTitle}
                            </label>
                            <textarea
                              id={`notes-${noteKey}`}
                              className={styles.notesInput}
                              value={noteValue}
                              onChange={
                                onCommentChange
                                  ? handleNotesChange(key, noteKey)
                                  : undefined
                              }
                              readOnly={!onCommentChange}
                              rows={2}
                              placeholder={t`Add your notes…`}
                            />
                          </Box>
                        )
                      }, questions)}
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          )
        }),
      )(fields)}
    </Form>
  )
}
